import type { NumberValue } from 'd3-scale';

import {
  type ContinuousScale,
  hasHorizontalLabelCollision,
} from './has-horizontal-label-collision.js';

const DEFAULT_MAX_ITERATIONS = 5;
export const DEFAULT_MIN_TICK_COUNT = 2;

export interface TickCandidate {
  formattedTicks: string[];
  rawTicks: NumberValue[];
}

export interface AdaptiveReductionOptions {
  maxIterations?: number;
  minTickCount?: number;
  /**
   * When provided, collision detection uses domain-aware alignment (AC1) instead
   * of the legacy index-based alignment. Pass `[domainMin, domainMax]` as numbers
   * (use `+` to convert Date values).
   */
  domainBounds?: [number, number];
}

/**
 * Adaptively reduces horizontal tick candidates until labels no longer
 * collide or the minimum tick count is reached.
 *
 * @param getCandidate - Called on each iteration with the 0-based attempt
 *   index. Must return the next candidate tick set. The callback is
 *   responsible for its own reduction strategy (e.g. doubling the step,
 *   halving the d3 tick count hint, etc.). Return `null` to signal that
 *   no further reduction is possible and the loop should stop.
 * @param scale - The d3 continuous scale used for pixel-position calculations.
 * @param options - Tuning knobs for the reduction loop.
 * @returns The first non-colliding candidate, or the last candidate when
 *   the loop exhausts all iterations / reaches the minimum tick count.
 */
export function adaptivelyReduceHorizontalTicks<T extends TickCandidate>(
  getCandidate: (attempt: number) => T | null,
  scale: ContinuousScale,
  options?: AdaptiveReductionOptions,
): T {
  const maxIterations = options?.maxIterations ?? DEFAULT_MAX_ITERATIONS;
  const minTickCount = options?.minTickCount ?? DEFAULT_MIN_TICK_COUNT;
  const domainBounds = options?.domainBounds;

  let current = getCandidate(0);

  if (current === null) {
    // Should not happen — caller must provide at least one candidate.
    // Return a safe empty result that satisfies the type constraint.
    return { formattedTicks: [], rawTicks: [] } as unknown as T;
  }

  for (let i = 1; i <= maxIterations; i++) {
    if (
      !hasHorizontalLabelCollision(
        current.formattedTicks,
        current.rawTicks,
        scale,
        domainBounds,
      )
    ) {
      return current;
    }

    // Count only visible (non-empty) labels so that log-scale minor ticks (which
    // render as empty strings) do not inflate the count and prevent early exit.
    const currentVisibleCount = current.formattedTicks.filter(
      (t) => t !== '',
    ).length;
    if (currentVisibleCount <= minTickCount) {
      return current;
    }

    const next = getCandidate(i);
    if (next === null) {
      return current;
    }
    const nextVisibleCount = next.formattedTicks.filter((t) => t !== '').length;
    if (nextVisibleCount < minTickCount) {
      return current;
    }

    current = next;
  }

  return current;
}
